import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiToggleLeft,
  FiToggleRight,
  FiMapPin,
  FiSearch,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiShield,
  FiAlertTriangle,
  FiCheckCircle,
  FiGlobe,
} from "react-icons/fi";
import { MdDeliveryDining } from "react-icons/md";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/bharat-karigari-api";

// ─── Indian States (for quick-select chips) ────────────────────────────────
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka",
  "Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram",
  "Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli",
  "Daman and Diu","Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

// ─── Tag Input Component ────────────────────────────────────────────────────
const TagInput = ({ label, tags, onAdd, onRemove, placeholder, quickOptions = [] }) => {
  const [input, setInput] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const filtered = quickOptions.filter(
    (o) => o.toLowerCase().includes(input.toLowerCase()) && !tags.includes(o)
  );

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && input.trim()) {
      e.preventDefault();
      onAdd(input.trim());
      setInput("");
      setShowDropdown(false);
    }
    if (e.key === "Backspace" && !input && tags.length) {
      onRemove(tags[tags.length - 1]);
    }
  };

  return (
    <div className="relative">
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        {label}
      </label>
      <div className="min-h-[48px] p-2 rounded-xl border border-gray-300 bg-white flex flex-wrap gap-2 focus-within:border-blue-500/60 transition-all">
        {tags.map((tag) => (
          <span
            key={tag}
            className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-500/20 text-blue-700 text-xs font-medium border border-blue-500/30"
          >
            {tag}
            <button
              type="button"
              onClick={() => onRemove(tag)}
              className="ml-1 text-blue-600 hover:text-red-600 transition-colors"
            >
              <FiX size={10} />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowDropdown(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[100px] bg-transparent outline-none text-gray-800 text-sm placeholder-gray-400"
        />
      </div>

      {/* Quick-select dropdown */}
      <AnimatePresence>
        {showDropdown && input && filtered.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-2xl max-h-48 overflow-y-auto"
          >
            {filtered.slice(0, 8).map((opt) => (
              <button
                key={opt}
                type="button"
                onMouseDown={() => { onAdd(opt); setInput(""); setShowDropdown(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-600 hover:bg-blue-500/20 hover:text-gray-800 transition-colors"
              >
                {opt}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-xs text-gray-500 mt-1">
        Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Enter</kbd> or <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">,</kbd> to add
      </p>
    </div>
  );
};

// ─── Zone Card ──────────────────────────────────────────────────────────────
const ZoneCard = ({ zone, onEdit, onDelete, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const isWhitelist = zone.mode === "whitelist";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
        zone.isActive
          ? "border-gray-300 bg-gradient-to-br from-white to-gray-50"
          : "border-gray-200 bg-gray-100/50 opacity-60"
      }`}
    >
      {/* Card Header */}
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div
            className={`p-2.5 rounded-xl ${
              isWhitelist
                ? "bg-emerald-500/15 text-emerald-600"
                : "bg-red-500/15 text-red-600"
            }`}
          >
            {isWhitelist ? <FiCheckCircle size={18} /> : <FiAlertTriangle size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-gray-800 font-semibold text-sm">{zone.name}</h3>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  isWhitelist
                    ? "bg-emerald-500/20 text-emerald-600"
                    : "bg-red-500/20 text-red-600"
                }`}
              >
                {isWhitelist ? "✅ Whitelist" : "🚫 Blacklist"}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  zone.isActive
                    ? "bg-blue-500/20 text-blue-600"
                    : "bg-gray-500/20 text-gray-500"
                }`}
              >
                {zone.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            {zone.description && (
              <p className="text-gray-500 text-xs mt-1 line-clamp-1">{zone.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              {zone.states?.length > 0 && (
                <span className="text-xs text-gray-500">
                  <span className="text-blue-600 font-medium">{zone.states.length}</span> States
                </span>
              )}
              {zone.cities?.length > 0 && (
                <span className="text-xs text-gray-500">
                  <span className="text-blue-600 font-medium">{zone.cities.length}</span> Cities
                </span>
              )}
              {zone.pincodes?.length > 0 && (
                <span className="text-xs text-gray-500">
                  <span className="text-blue-600 font-medium">{zone.pincodes.length}</span> Pincodes
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onToggle(zone._id)}
            className={`p-2 rounded-lg transition-all ${
              zone.isActive
                ? "text-emerald-600 hover:bg-emerald-500/10"
                : "text-gray-500 hover:bg-white"
            }`}
            title={zone.isActive ? "Deactivate" : "Activate"}
          >
            {zone.isActive ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
          </button>
          <button
            onClick={() => onEdit(zone)}
            className="p-2 rounded-lg text-blue-600 hover:bg-blue-500/10 transition-all"
            title="Edit"
          >
            <FiEdit2 size={16} />
          </button>
          <button
            onClick={() => onDelete(zone._id)}
            className="p-2 rounded-lg text-red-600 hover:bg-red-500/10 transition-all"
            title="Delete"
          >
            <FiTrash2 size={16} />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-lg text-gray-500 hover:bg-white transition-all"
          >
            {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-gray-200 space-y-4">
              {zone.states?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">States</p>
                  <div className="flex flex-wrap gap-1.5">
                    {zone.states.map((s) => (
                      <span key={s} className="px-2.5 py-1 bg-blue-500/10 text-blue-700 rounded-lg text-xs">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {zone.cities?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Cities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {zone.cities.map((c) => (
                      <span key={c} className="px-2.5 py-1 bg-violet-500/10 text-violet-700 rounded-lg text-xs">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {zone.pincodes?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">PIN Codes</p>
                  <div className="flex flex-wrap gap-1.5">
                    {zone.pincodes.map((p) => (
                      <span key={p} className="px-2.5 py-1 bg-amber-500/10 text-amber-700 rounded-lg text-xs font-mono">{p}</span>
                    ))}
                  </div>
                </div>
              )}
              {zone.notDeliverableMessage && (
                <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/10">
                  <p className="text-xs text-gray-500 mb-1 font-semibold">Message shown to user:</p>
                  <p className="text-sm text-red-700 italic">"{zone.notDeliverableMessage}"</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Zone Form Modal ────────────────────────────────────────────────────────
const ZoneFormModal = ({ editZone, onClose, onSaved }) => {
  const isEdit = !!editZone;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    mode: "blacklist",
    states: [],
    cities: [],
    pincodes: [],
    isActive: true,
    notDeliverableMessage: "Sorry, delivery is not available at your location.",
    priority: 0,
  });

  useEffect(() => {
    if (editZone) {
      setForm({
        name: editZone.name || "",
        description: editZone.description || "",
        mode: editZone.mode || "blacklist",
        states: editZone.states || [],
        cities: editZone.cities || [],
        pincodes: editZone.pincodes || [],
        isActive: editZone.isActive !== undefined ? editZone.isActive : true,
        notDeliverableMessage: editZone.notDeliverableMessage || "",
        priority: editZone.priority || 0,
      });
    }
  }, [editZone]);

  const addTag = (field, value) => {
    if (!form[field].includes(value)) {
      setForm((prev) => ({ ...prev, [field]: [...prev[field], value] }));
    }
  };
  const removeTag = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: prev[field].filter((t) => t !== value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Zone name is required");

    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API_BASE}/delivery-zones/${editZone._id}`, form);
        toast.success("Delivery zone updated!");
      } else {
        await axios.post(`${API_BASE}/delivery-zones/`, form);
        toast.success("Delivery zone created!");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-2xl bg-white border border-gray-300 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Modal Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-300 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-500/20 rounded-xl text-blue-600">
              <FiMapPin size={18} />
            </div>
            <div>
              <h2 className="text-gray-800 font-bold text-lg">
                {isEdit ? "Edit Delivery Zone" : "Create Delivery Zone"}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">
                Configure where products can/cannot be delivered
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-white hover:bg-gray-100 text-gray-500 transition-all"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Name & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Zone Name *
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., North India Block"
                required
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-blue-500/60 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Priority (higher = checked first)
              </label>
              <input
                type="number"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                placeholder="0"
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-blue-500/60 transition-all"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Description (optional)
            </label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief note about this zone..."
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-blue-500/60 transition-all"
            />
          </div>

          {/* Mode Selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Zone Mode
            </label>
            <div className="grid grid-cols-2 gap-3">
              {["whitelist", "blacklist"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, mode: m })}
                  className={`relative p-4 rounded-xl border text-left transition-all ${
                    form.mode === m
                      ? m === "whitelist"
                        ? "border-emerald-500/60 bg-emerald-500/10"
                        : "border-red-500/60 bg-red-500/10"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {m === "whitelist" ? (
                      <FiCheckCircle className="text-emerald-600" size={16} />
                    ) : (
                      <FiAlertTriangle className="text-red-600" size={16} />
                    )}
                    <span
                      className={`font-semibold text-sm capitalize ${
                        m === "whitelist" ? "text-emerald-600" : "text-red-600"
                      }`}
                    >
                      {m}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {m === "whitelist"
                      ? "Only listed locations CAN receive delivery"
                      : "Listed locations CANNOT receive delivery"}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Location Inputs */}
          <TagInput
            label="States"
            tags={form.states}
            onAdd={(v) => addTag("states", v)}
            onRemove={(v) => removeTag("states", v)}
            placeholder="Type a state name and press Enter..."
            quickOptions={INDIAN_STATES}
          />

          <TagInput
            label="Cities"
            tags={form.cities}
            onAdd={(v) => addTag("cities", v)}
            onRemove={(v) => removeTag("cities", v)}
            placeholder="Type a city name and press Enter..."
          />

          <TagInput
            label="PIN Codes"
            tags={form.pincodes}
            onAdd={(v) => addTag("pincodes", v)}
            onRemove={(v) => removeTag("pincodes", v)}
            placeholder="Type a pincode and press Enter..."
          />

          {/* Custom Message */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Message shown to customer when delivery unavailable
            </label>
            <textarea
              value={form.notDeliverableMessage}
              onChange={(e) => setForm({ ...form, notDeliverableMessage: e.target.value })}
              rows={2}
              placeholder="Sorry, delivery is not available at your location."
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-blue-500/60 transition-all resize-none"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-300">
            <div>
              <p className="text-gray-800 text-sm font-medium">Zone Active</p>
              <p className="text-gray-500 text-xs mt-0.5">
                Inactive zones are ignored during delivery checks
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, isActive: !form.isActive })}
              className={`w-12 h-6 rounded-full transition-all relative ${
                form.isActive ? "bg-blue-500" : "bg-gray-600"
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                  form.isActive ? "left-7" : "left-1"
                }`}
              />
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-300 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-white hover:bg-gray-100 text-gray-600 text-sm font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl bg-[#b07d17] hover:bg-[#8c6312] text-white text-sm font-semibold transition-all disabled:opacity-60 flex items-center gap-2"
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin" />
            ) : null}
            {isEdit ? "Save Changes" : "Create Zone"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Check Delivery Tool ────────────────────────────────────────────────────
const CheckDeliveryTool = () => {
  const [form, setForm] = useState({ state: "", city: "", pincode: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCheck = async () => {
    if (!form.state && !form.city && !form.pincode) {
      return toast.error("Enter at least one field to check");
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API_BASE}/delivery-zones/check`, form);
      setResult(data);
    } catch (err) {
      toast.error("Check failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 rounded-2xl border border-gray-300 bg-gradient-to-br from-gray-50 to-transparent">
      <div className="flex items-center gap-2 mb-4">
        <FiSearch className="text-blue-600" size={16} />
        <h3 className="text-gray-800 font-semibold text-sm">Test Delivery Serviceability</h3>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        {["state", "city", "pincode"].map((field) => (
          <input
            key={field}
            value={form[field]}
            onChange={(e) => setForm({ ...form, [field]: e.target.value })}
            placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
            className="px-3 py-2 bg-white border border-gray-300 rounded-xl text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-blue-500/60 transition-all"
          />
        ))}
      </div>
      <button
        onClick={handleCheck}
        disabled={loading}
        className="w-full py-2.5 rounded-xl bg-[#b07d17] hover:bg-[#8c6312] text-white text-sm font-medium transition-all disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? (
          <span className="w-4 h-4 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin" />
        ) : (
          <FiSearch size={14} />
        )}
        Check Delivery
      </button>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-3 p-4 rounded-xl border ${
              result.deliverable
                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700"
                : "bg-red-500/10 border-red-500/20 text-red-700"
            }`}
          >
            <div className="flex items-center gap-2 font-semibold text-sm">
              {result.deliverable ? <FiCheckCircle size={16} /> : <FiAlertTriangle size={16} />}
              {result.deliverable ? "✅ Delivery Available" : "🚫 Not Deliverable"}
            </div>
            <p className="text-xs mt-1 opacity-80">{result.reason}</p>
            {result.failedZone && (
              <p className="text-xs mt-0.5 opacity-60">Failed Zone: {result.failedZone}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const AdminDeliveryZoneManager = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editZone, setEditZone] = useState(null);
  const [filterMode, setFilterMode] = useState("all");

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/delivery-zones/`);
      setZones(data.zones || []);
    } catch {
      toast.error("Failed to load delivery zones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this delivery zone?")) return;
    try {
      await axios.delete(`${API_BASE}/delivery-zones/${id}`);
      toast.success("Zone deleted");
      fetchZones();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleToggle = async (id) => {
    try {
      const { data } = await axios.patch(`${API_BASE}/delivery-zones/${id}/toggle`);
      toast.success(data.message);
      fetchZones();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const handleEdit = (zone) => {
    setEditZone(zone);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditZone(null);
    setShowModal(true);
  };

  const filteredZones = zones.filter((z) => {
    const matchesSearch =
      !searchQuery ||
      z.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      z.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesMode = filterMode === "all" || z.mode === filterMode;
    return matchesSearch && matchesMode;
  });

  const activeCount = zones.filter((z) => z.isActive).length;
  const whitelistCount = zones.filter((z) => z.mode === "whitelist").length;
  const blacklistCount = zones.filter((z) => z.mode === "blacklist").length;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur border-b border-gray-300 px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-500/20 rounded-xl">
              <MdDeliveryDining size={22} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Delivery Zone Manager</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Control where your products can be delivered — like Amazon & Flipkart
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAdd}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#b07d17] hover:bg-[#8c6312] text-white text-sm font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/30"
          >
            <FiPlus size={16} />
            Add Zone
          </motion.button>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6 max-w-6xl">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Zones", value: zones.length, icon: <FiGlobe size={18} />, color: "indigo" },
            { label: "Active Zones", value: activeCount, icon: <FiCheckCircle size={18} />, color: "emerald" },
            { label: "Whitelist", value: whitelistCount, icon: <FiShield size={18} />, color: "violet" },
            { label: "Blacklist", value: blacklistCount, icon: <FiAlertTriangle size={18} />, color: "red" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="p-4 rounded-2xl border border-gray-300 bg-gray-100 flex items-center gap-3"
            >
              <div className={`p-2 rounded-xl bg-${stat.color}-500/15 text-${stat.color}-400`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Check Delivery Tool */}
        <CheckDeliveryTool />

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <FiSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search zones..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-blue-500/60 transition-all"
            />
          </div>
          <div className="flex gap-2">
            {["all", "whitelist", "blacklist"].map((m) => (
              <button
                key={m}
                onClick={() => setFilterMode(m)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize transition-all ${
                  filterMode === m
                    ? "bg-[#b07d17] text-white"
                    : "bg-white text-gray-500 hover:bg-gray-100"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Zones List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : filteredZones.length === 0 ? (
          <div className="py-20 text-center">
            <MdDeliveryDining size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No delivery zones configured</p>
            <p className="text-gray-500 text-sm mt-1">
              {searchQuery ? "No zones match your search." : "Create your first zone to control delivery locations."}
            </p>
            {!searchQuery && (
              <button
                onClick={handleAdd}
                className="mt-4 px-5 py-2.5 bg-[#b07d17] hover:bg-[#8c6312] text-gray-800 text-sm font-medium rounded-xl transition-all"
              >
                Create First Zone
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {filteredZones.map((zone) => (
                <ZoneCard
                  key={zone._id}
                  zone={zone}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <ZoneFormModal
            editZone={editZone}
            onClose={() => { setShowModal(false); setEditZone(null); }}
            onSaved={fetchZones}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDeliveryZoneManager;
